import { useParams } from "react-router";
import { AddDesignerFileLayout } from "../layout";
import { AddProjectContainer } from "./add-project-container";
import { useGetProjectFileQuery } from "../../../hooks/use-get-project-file-query";
import { useEffect, useRef, useState } from "react";
import { useDashboardFooter } from "../../../contexts/DashboardFooterContext";
import { Button, Stack } from "@mui/material";

export const AddProjectView = () => {
  const params = useParams();
  const isEditMode = !!params?.id;
  const title = isEditMode ? "Edit Project File" : "Add a New Project File";
  const { getProjectFile, loading, projectFile } = useGetProjectFileQuery();
  const { setFooter, clearFooter } = useDashboardFooter();
  
  // Use ref instead of state to avoid re-renders
  const handleSubmitRef = useRef<(() => void) | null>(null);
  const handlePublishRef = useRef<((nextPublished: boolean) => void) | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (!hasFetched.current && isEditMode) {
      getProjectFile(params.id as string, true).catch((error) => {
        console.error("Error fetching project file:", error);
      });
      hasFetched.current = true;
    }
  }, [isEditMode, getProjectFile, params]);

  // Set up the fixed footer
  useEffect(() => {
    const handleAddProject = () => {
      if (handleSubmitRef.current) {
        handleSubmitRef.current();
      }
    };

    const handlePublishToggle = () => {
      if (handlePublishRef.current) {
        handlePublishRef.current(!isPublished);
      }
    };

    const footerContent = (
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} sx={{ width: "100%" }}>
        <Button
          onClick={handlePublishToggle}
          variant="outlined"
          disabled={!isFormValid}
          sx={{
            borderColor: "#000",
            color: "#000",
            px: 4,
            "&:hover": {
              borderColor: "#333",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "&:disabled": {
              borderColor: "#ccc",
              color: "#666",
            },
          }}
        >
          {isPublished ? "Unpublish" : "Publish"}
        </Button>
        <Button
          onClick={handleAddProject}
          variant="contained"
          disabled={!isFormValid}
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            px: 4,
            "&:hover": {
              backgroundColor: "#333",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
              color: "#666",
            },
          }}
        >
          {isEditMode ? "Update Project" : "Add Project"}
        </Button>
      </Stack>
    );

    setFooter(footerContent);

    // Cleanup footer when component unmounts
    return () => {
      clearFooter();
    };
  }, [setFooter, clearFooter, isFormValid, isEditMode, isPublished]);

  return (
    <AddDesignerFileLayout
      title={title}
      subTitle="A project is a collection of photos of your work. Photos helps sell to the homeowner."
    >
      {(!isEditMode || !loading) && (
        <AddProjectContainer 
          projectFile={projectFile} 
          handleSubmitRef={handleSubmitRef}
          handlePublishRef={handlePublishRef}
          onPublishStateChange={setIsPublished}
          onSetFormValid={setIsFormValid}
        />
      )}
    </AddDesignerFileLayout>
  );
};
